import React from 'react';
import { useForm } from 'react-hook-form';
import { AuthContextType } from '../../auth/AuthContext';
import { generateTimeOptions } from '../../../shared/lib/helpers';
import {
    fetchItemSchedules,
    createItemSchedule,
    updateItemSchedule,
    deleteItemSchedule,
} from '../../../api/schedule';
import { ItemSchedulePayload } from '../../../api/schedule';

type ItemScheduleFormValues = Omit<ItemSchedulePayload, 'is_enabled'> & {
    is_enabled: 'true' | 'false';
};

interface ItemScheduleFormProps {
    auth: AuthContextType;
    itemId: string;
}

interface ScheduleData {
    id: string;
    time_start: string;
    time_end: string;
    is_mon: boolean;
    is_tue: boolean;
    is_wed: boolean;
    is_thu: boolean;
    is_fri: boolean;
    is_sat: boolean;
    is_sun: boolean;
    price_ll_value: number;
    price_ll_value_type: string;
    price_default_value: number;
    price_default_value_type: string;
    is_enabled: boolean;
}

const ItemScheduleForm: React.FC<ItemScheduleFormProps> = ({ auth, itemId }) => {
    const [schedules, setSchedules] = React.useState<ScheduleData[]>([]);
    const [, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [isAllDay, setIsAllDay] = React.useState(true);
    const [isNextDay, setIsNextDay] = React.useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        formState: {},
        watch,
        reset
    } = useForm<ItemScheduleFormValues>({
        defaultValues: {
            item: itemId,
            time_start: '00:00',
            time_end: '00:00',
            is_mon: false,
            is_tue: false,
            is_wed: false,
            is_thu: false,
            is_fri: false,
            is_sat: false,
            is_sun: false,
            price_ll_value: 0,
            price_ll_value_type: 'amount',
            price_default_value: 0,
            price_default_value_type: 'amount',
            is_enabled: 'true',
        }
    });

    const timeStart = watch('time_start');
    const timeEnd = watch('time_end');

    React.useEffect(() => {
        if (timeStart && timeEnd) {
            const startTimeValue = parseInt(timeStart.replace(':', ''));
            const endTimeValue = parseInt(timeEnd.replace(':', ''));
            const isAllDaySchedule = timeStart === '00:00' && timeEnd === '00:00';
            
            if (isAllDaySchedule) {
                setIsAllDay(true);
                setIsNextDay(false);
            } else if (startTimeValue > endTimeValue) {
                setIsAllDay(false);
                setIsNextDay(true);
            } else {
                setIsAllDay(false);
                setIsNextDay(false);
            }
        }
    }, [timeStart, timeEnd]);



    const handleAllDayChange = (checked: boolean) => {
        setIsAllDay(checked);
        if (checked) {
            setValue('time_start', '00:00');
            setValue('time_end', '00:00');
        }
    };

    const loadSchedules = React.useCallback(() => {
        setIsLoading(true);
        fetchItemSchedules({
            auth,
            itemId,
            setErrorMessage,
            setResult: (data) => {
                if (Array.isArray(data)) {
                    setSchedules(data);
                } else if (data?.results) {
                    setSchedules(data.results);
                }
            }
        }).finally(() => setIsLoading(false));
    }, [auth, itemId]);

    React.useEffect(() => {
        loadSchedules();
    }, [loadSchedules]);

    const onSubmit = async (data: ItemScheduleFormValues) => {
        setErrorMessage(null);
        setProcessMessage('저장 중입니다...');

        try {
            const payload: ItemSchedulePayload = {
                ...data,
                is_enabled: data.is_enabled === 'true',
            };

            if (editingId) {
                await updateItemSchedule({
                    auth,
                    id: editingId,
                    payload,
                    setErrorMessage,
                    setProcessMessage,
                    setResult: () => {
                        loadSchedules();
                        reset();
                        setEditingId(null);
                    },
                    setError: () => { }
                });
            } else {
                await createItemSchedule({
                    auth,
                    payload,
                    setErrorMessage,
                    setProcessMessage,
                    setResult: () => {
                        loadSchedules();
                        reset();
                    },
                    setError: () => { }
                });
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                auth?.logout();
            }
        } finally {
            setProcessMessage(null);
        }
    };

    const formatTimeForInput = (timeStr: string): string => {
        if (!timeStr) return '';
        return timeStr.substring(0, 5);
    };

    const handleEdit = (schedule: ScheduleData) => {
        setEditingId(schedule.id);
        const startTime = formatTimeForInput(schedule.time_start);
        const endTime = formatTimeForInput(schedule.time_end);
        setValue('time_start', startTime);
        setValue('time_end', endTime);
        
        const isAllDaySchedule = startTime === '00:00' && endTime === '00:00';
        setIsAllDay(isAllDaySchedule);
        
        setValue('is_mon', schedule.is_mon);
        setValue('is_tue', schedule.is_tue);
        setValue('is_wed', schedule.is_wed);
        setValue('is_thu', schedule.is_thu);
        setValue('is_fri', schedule.is_fri);
        setValue('is_sat', schedule.is_sat);
        setValue('is_sun', schedule.is_sun);
        setValue('price_ll_value', schedule.price_ll_value);
        setValue('price_ll_value_type', schedule.price_ll_value_type);
        setValue('price_default_value', schedule.price_default_value);
        setValue('price_default_value_type', schedule.price_default_value_type);
        setValue('is_enabled', schedule.is_enabled ? 'true' : 'false');
    };

    const handleDelete = async (id: string) => {
        if (!confirm('삭제하시겠습니까?')) return;

        setProcessMessage('삭제 중입니다...');
        try {
            await deleteItemSchedule({
                auth,
                id,
                setErrorMessage,
                setResult: () => loadSchedules()
            });
        } catch (error: any) {
            if (error.response?.status === 401) {
                auth?.logout();
            }
        } finally {
            setProcessMessage(null);
        }
    };

    const handleCancel = () => {
        reset();
        setEditingId(null);
        setIsAllDay(true);
        setIsNextDay(false);
    };

    const getDayString = (schedule: ScheduleData): string => {
        const days = [];
        if (schedule.is_mon) days.push('월');
        if (schedule.is_tue) days.push('화');
        if (schedule.is_wed) days.push('수');
        if (schedule.is_thu) days.push('목');
        if (schedule.is_fri) days.push('금');
        if (schedule.is_sat) days.push('토');
        if (schedule.is_sun) days.push('일');
        return days.length > 0 ? days.join('') : '없음';
    };

    const formatTime = (timeStr: string, isEndTime: boolean = false): string => {
        if (!timeStr) return '';
        const time = timeStr.substring(0, 5);
        return isEndTime && time === '00:00' ? '24:00' : time;
    };

    return (<>
        {errorMessage && (
            <div className="alert alert-danger" role="alert">
                {errorMessage}
            </div>
        )}
        {processMessage && (
            <div className="alert alert-info" role="alert">
                {processMessage}
            </div>
        )}

        <div className="row mb-1">
            <div className="col">
                스케줄 목록
            </div>
            <div className="col">
                {editingId ? '스케줄 수정' : '스케줄 추가'}
            </div>
        </div>

        <div className="row">
            <div className="col">
                <table className="table">
                    <thead>
                        <tr>
                            <th className="fw-normal">#</th>
                            <th className="fw-normal">요일</th>
                            <th className="fw-normal">시작</th>
                            <th className="fw-normal">종료</th>
                            <th className="fw-normal">하한가</th>
                            <th className="fw-normal">복원가</th>
                            <th className="fw-normal">활성</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {schedules.length > 0 ? (
                            schedules.map((schedule, idx) => (
                                <tr key={schedule.id}>
                                    <td>{idx + 1}</td>
                                    <td>{getDayString(schedule)}</td>
                                    <td>{formatTime(schedule.time_start, false)}</td>
                                    <td>{formatTime(schedule.time_end, true)}</td>
                                    <td>{schedule.price_ll_value.toLocaleString()}{schedule.price_ll_value_type === 'percent' ? '%' : '원'}</td>
                                    <td>{schedule.price_default_value.toLocaleString()}{schedule.price_default_value_type === 'percent' ? '%' : '원'}</td>
                                    <td>{schedule.is_enabled ? '활성' : '비활성'}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleEdit(schedule)}
                                        >
                                            수정
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger ms-1"
                                            onClick={() => handleDelete(schedule.id)}
                                        >
                                            삭제
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={8}
                                    className="text-center align-middle"
                                    style={{ height: '5rem' }}
                                >
                                    등록된 스케줄이 없습니다.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="form-text">
                    상품그룹, 전역 설정 스케줄보다 우선 적용됩니다.
                </div>
                <div className="form-text">
                    동일 시간대에 여러 스케줄이 존재할 경우, 가장 먼저 등록된 스케줄이 우선 적용됩니다.
                </div>
            </div>
            <div className="col">
                <ul className="list-unstyled mt-2">
                    <li>
                        <label className="form-label">적용요일</label>
                        <table className="table table-m w-auto">
                            <tbody>
                                <tr>
                                    <th className="fw-normal">월</th>
                                    <th className="fw-normal">화</th>
                                    <th className="fw-normal">수</th>
                                    <th className="fw-normal">목</th>
                                    <th className="fw-normal">금</th>
                                    <th className="fw-normal">토</th>
                                    <th className="fw-normal">일</th>
                                </tr>
                                <tr>
                                    <td><input type="checkbox" {...register('is_mon')} /></td>
                                    <td><input type="checkbox" {...register('is_tue')} /></td>
                                    <td><input type="checkbox" {...register('is_wed')} /></td>
                                    <td><input type="checkbox" {...register('is_thu')} /></td>
                                    <td><input type="checkbox" {...register('is_fri')} /></td>
                                    <td><input type="checkbox" {...register('is_sat')} /></td>
                                    <td><input type="checkbox" {...register('is_sun')} /></td>
                                </tr>
                            </tbody>
                        </table>
                    </li>
                    <li className="mb-3 d-flex gap-4">
                        <div>
                            <input
                                type="checkbox"
                                id="is_all_day"
                                checked={isAllDay}
                                onChange={(e) => handleAllDayChange(e.target.checked)}
                            />
                            <label className="form-check-label ms-2" htmlFor="is_all_day">하루종일</label>
                        </div>
                        <div>
                            <input
                                type="checkbox"
                                id="is_next_day"
                                checked={isNextDay}
                                disabled={true}
                            />
                            <label className="form-check-label ms-2" htmlFor="is_next_day">다음 날</label>
                        </div>
                    </li>
                    <li className="mb-3 row">
                        <div className="col">
                            <label className="form-label">시작시각</label>
                            <select className="form-select" {...register('time_start')} disabled={isAllDay}>
                                {generateTimeOptions('00:00', '23:50')}
                            </select>
                        </div>
                        <div className="col">
                            <label className="form-label">종료시각</label>
                            <select className="form-select" {...register('time_end')} disabled={isAllDay}>
                                {generateTimeOptions('00:10', '24:00')}
                            </select>
                        </div>
                    </li>
                    <li className="mb-3">
                        <span className="form-text">
                            00:00 - 00:00 으로 설정할 경우, 해당 요일은 하루종일 스케줄이 적용됩니다.<br />
                            00:00 - 00:00 이외의 시간대에서 시작시각이 종료시각과 같게 설정할 수 없습니다.
                        </span>
                    </li>
                    <li className="mb-3 row">
                        <div className="col">
                            <label className="form-label">하한가 조정 (원)</label>
                            <input type="number" className="form-control" {...register('price_ll_value', { valueAsNumber: true })} />
                            <small className="form-text text-muted">기본 하한가를 대체 (0으로 설정 시 대체없음)</small>
                        </div>
                        <div className="col">
                            <label className="form-label">복원가 조정 (원)</label>
                            <input type="number" className="form-control" {...register('price_default_value', { valueAsNumber: true })} />
                            <small className="form-text text-muted">기본 복원가를 대체 (0으로 설정 시 대체없음)</small>
                        </div>
                    </li>
                    <li className="mb-3">
                        <label className="form-label">활성화 여부</label>
                        <div>
                            <input
                                type="radio"
                                className="form-check-input"
                                value="true"
                                id="is_enabled_true"
                                {...register('is_enabled')}
                            />
                            <label className="form-check-label ms-2" htmlFor="is_enabled_true">활성</label>
                            <input
                                type="radio"
                                className="form-check-input ms-3"
                                value="false"
                                id="is_enabled_false"
                                {...register('is_enabled')}
                            />
                            <label className="form-check-label ms-2" htmlFor="is_enabled_false">비활성</label>
                        </div>
                    </li>
                </ul>
                <div className="d-flex gap-2 justify-content-between">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit(onSubmit)}
                    >
                        {editingId ? '변경' : '추가'}
                    </button>
                    {editingId && (
                        <button type="button" className="btn btn-outline-secondary" onClick={handleCancel}>
                            취소
                        </button>
                    )}
                </div>
            </div>
        </div>
    </>);
};

export default ItemScheduleForm;
